import { toast } from "material-react-toastify";

const AUTH_TOKEN_KEY = "_chuck-38e6e39a-ab69-4aac-bb0c";

export type SsoErrorCode =
  | "INVALID_SESSION"
  | "INVALID_ORIGIN"
  | "INVALID_REQUEST"
  | "INTERNAL_ERROR"
  | "INVALID_OR_EXPIRED_REQUEST"
  | "INVALID_CREDENTIALS"
  | "RATE_LIMITED"
  | "SERVICE_UNAVAILABLE"
  | "SSO_REQUEST_FAILED";

interface SsoResponse {
  success?: boolean;
  start_url?: string;
  continue_url?: string;
  error_code?: SsoErrorCode;
}

export class SsoRequestError extends Error {
  constructor(
    public readonly code: SsoErrorCode,
    public readonly status: number,
    public readonly retryAfterSeconds?: number
  ) {
    super(code);
    this.name = "SsoRequestError";
  }
}

export function getSsoApiBase(): string {
  const gqlUrl = process.env.REACT_APP_GQL_URL;
  if (!gqlUrl) throw new Error("REACT_APP_GQL_URL is not configured");

  try {
    return new URL(gqlUrl).origin;
  } catch {
    throw new Error("REACT_APP_GQL_URL must be an absolute URL");
  }
}

function authorizationValue(accessToken: string): string {
  return /^Bearer\s/i.test(accessToken)
    ? accessToken
    : `Bearer ${accessToken}`;
}

function retryAfterSeconds(response: Response): number | undefined {
  const value = response.headers.get("Retry-After");
  if (!value) return undefined;

  const seconds = Number(value);
  if (Number.isFinite(seconds)) return Math.max(0, Math.min(seconds, 300));

  const date = Date.parse(value);
  if (Number.isNaN(date)) return undefined;
  return Math.max(0, Math.min(Math.ceil((date - Date.now()) / 1000), 300));
}

async function parseResponse(response: Response): Promise<SsoResponse> {
  try {
    return (await response.json()) as SsoResponse;
  } catch {
    return {};
  }
}

async function request(
  path: string,
  init: RequestInit
): Promise<SsoResponse> {
  const response = await fetch(`${getSsoApiBase()}${path}`, {
    ...init,
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      ...init.headers,
    },
  });
  const result = await parseResponse(response);

  if (!response.ok) {
    throw new SsoRequestError(
      result.error_code || "SSO_REQUEST_FAILED",
      response.status,
      retryAfterSeconds(response)
    );
  }

  return result;
}

export function getStoredAccessToken(): string {
  return localStorage.getItem(AUTH_TOKEN_KEY) || "";
}

export function clearStoredAccessToken(): void {
  localStorage.removeItem(AUTH_TOKEN_KEY);
}

export async function bootstrapSsoSession(
  accessToken: string,
  input: { requestId: string } | { flow: "silent" } | Record<string, never> = {}
): Promise<SsoResponse> {
  const body = "requestId" in input
    ? { request_id: input.requestId }
    : input;
  const result = await request("/api/sso/session/bootstrap", {
    method: "POST",
    headers: { Authorization: authorizationValue(accessToken) },
    body: JSON.stringify(body),
  });

  const requiresContinueUrl = "requestId" in input;
  if (result.success !== true || (requiresContinueUrl ? !result.continue_url : !result.start_url)) {
    throw new SsoRequestError("SSO_REQUEST_FAILED", 200);
  }
  return result;
}

export async function deleteSsoSession(): Promise<void> {
  await request("/api/sso/session", { method: "DELETE" });
}

export async function loginToSso(input: {
  requestId: string;
  mobile: string;
  password: string;
}): Promise<SsoResponse> {
  const result = await request("/api/sso/ewingspapers/login", {
    method: "POST",
    body: JSON.stringify({
      request_id: input.requestId,
      mobile: input.mobile.trim(),
      password: input.password,
    }),
  });

  if (result.success !== true || !result.continue_url) {
    throw new SsoRequestError("SSO_REQUEST_FAILED", 200);
  }
  return result;
}

export async function launchExamsSso(accessToken?: string): Promise<void> {
  if (!accessToken) {
    window.open("https://ewingspapers.lk", "_blank", "noopener,noreferrer");
    return;
  }

  const result = await bootstrapSsoSession(accessToken, { flow: "silent" });
  if (!result.start_url) {
    throw new SsoRequestError("SSO_REQUEST_FAILED", 200);
  }

  window.open(result.start_url, "_blank", "noopener,noreferrer");
}

export function handleExamsSsoError(error: unknown): void {
  if (error instanceof SsoRequestError && error.code === "INVALID_SESSION") {
    clearStoredAccessToken();
    window.location.assign("/auth/login");
    return;
  }

  if (error instanceof SsoRequestError && error.code === "INVALID_ORIGIN") {
    toast.error("Exams sign-in is unavailable because of a configuration problem.");
  } else if (error instanceof SsoRequestError && error.code === "INVALID_REQUEST") {
    toast.error("Exams sign-in request is invalid. Please refresh and try again.");
  } else {
    toast.error("Unable to start Exams sign-in. Please try again.");
  }
}

